import React, { useRef, useCallback, useEffect, useState, Fragment, startTransition, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { Input } from 'reactstrap';
import classnames from 'classnames';

import './autosized-area.scss';

export const AUTOSIZED_ID = 'autosized-area';

export const AutosizedArea = memo(
  ({
    onChange,
    onBlur,
    hintText,
    inputValue,
    className,
    inputMinHeight,
    disableOutline,
    innerInputRef,
    additionalScrollHeight,
    withShadow,
    isCollapsed,
    ...rest
  }) => {
    const ref = useRef();
    const inputRef = innerInputRef || ref;
    const [disabled, setDisabled] = useState(true);
    const [currentInputHeight, setCurrentInputHeight] = useState(inputMinHeight);

    useEffect(() => {
      setDisabled(false);
    }, []);

    useEffect(() => {
      if (isCollapsed) {
        setCurrentInputHeight(inputMinHeight);
      } else {
        inputRef.current.style.setProperty('height', `${inputMinHeight}px`, 'important'); // this is for correct height when deleting a line
        const height = Math.max(inputMinHeight, inputRef.current.scrollHeight + additionalScrollHeight);
        inputRef.current.style.height = `${height}px`;
        startTransition(() => {
          setCurrentInputHeight(height);
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, isCollapsed, inputMinHeight, inputRef]);

    const handleInputChange = useCallback(
      ({ target: { value } }) => {
        onChange(value);
      },
      [onChange]
    );

    const handleBlur = useCallback(
      event => {
        inputRef.current.scrollTop = 0;
        onBlur(event);
      },
      [inputRef, onBlur]
    );

    const style = useMemo(
      () => ({
        height: `${currentInputHeight}px`,
      }),
      [currentInputHeight]
    );

    return (
      <Fragment>
        <Input
          type="textarea"
          name="query"
          autoComplete="off"
          placeholder={hintText}
          className={classnames('autosized-area-field', className, {
            'collapsed pt-0': isCollapsed,
            'with-shadow': withShadow && currentInputHeight > inputMinHeight,
            'disable-outline': disableOutline,
          })}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          innerRef={inputRef}
          style={style}
          disabled={disabled}
          {...rest}
        />
      </Fragment>
    );
  }
);

AutosizedArea.displayName = 'AutosizedArea';

AutosizedArea.propTypes = {
  inputMinHeight: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  hintText: PropTypes.string,
  maxLength: PropTypes.number,
  id: PropTypes.string,
  inputValue: PropTypes.string,
  className: PropTypes.string,
  disableOutline: PropTypes.bool,
  innerInputRef: PropTypes.shape({}),
  additionalScrollHeight: PropTypes.number,
  withShadow: PropTypes.bool,
  isCollapsed: PropTypes.bool,
};

AutosizedArea.defaultProps = {
  onChange: noop,
  onKeyDown: noop,
  onFocus: noop,
  onBlur: noop,
  hintText: '',
  maxLength: 200,
  id: AUTOSIZED_ID,
  inputValue: '',
  className: '',
  disableOutline: false,
  innerInputRef: undefined,
  additionalScrollHeight: 0,
  withShadow: false,
  isCollapsed: false,
};
